class WaveSliderComponent extends HTMLElement {
  constructor() {
    super();
    
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    
    if (!this.slider || !this.nextButton) return;
    
    this.isVertical = false;
    
    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }
  
  init() {
    const resizeObserver = new ResizeObserver(() => {
      this.checkOrientation();
      this.initPages();
    });
    resizeObserver.observe(this.slider);
    
    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    
    this.checkOrientation();
  }
  
  checkOrientation() {
    this.isVertical = window.innerWidth >= 750;
    this.slider.style.overflowX = this.isVertical ? 'hidden' : 'auto';
    this.slider.style.overflowY = this.isVertical ? 'auto' : 'hidden';
    this.slider.style.flexDirection = this.isVertical ? 'column' : 'row';
  }
  
  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientHeight > 0);
    if (this.sliderItemsToShow.length < 2) return;
    
    if (this.isVertical) {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
      this.slidesPerPage = Math.floor(this.slider.clientHeight / this.sliderItemOffset);
    } else {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
    }
    
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }
  
  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }
  
  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round((this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft) / this.sliderItemOffset) + 1;
    
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }
    
    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }
    
    if (this.currentPage === 1 && this.currentPage === this.totalPages) {
      this.classList.add('slider--no-buttons');
    } else {
      this.classList.remove('slider--no-buttons');
    }
    
    if (this.enableSliderLooping) return;
    
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
    
    if (isFirstSlide || this.isSlideVisible(this.sliderItemsToShow[0])) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }
    
    if (isLastSlide || this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }
  
  isSlideVisible(element, offset = 0) {
    if (this.isVertical) {
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return (element.offsetTop + element.clientHeight) <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
    } else {
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }
  }
  
  onButtonClick(event) {
    event.preventDefault();
    
    const step = event.currentTarget.dataset.step || 1;
    const scrollProperty = this.isVertical ? 'scrollTop' : 'scrollLeft';
    const scrollOffset = this.isVertical ? 'scrollHeight' : 'scrollWidth';
    
    this.slideScrollPosition = event.currentTarget.name === 'next' 
      ? this.slider[scrollProperty] + (step * this.sliderItemOffset) 
      : this.slider[scrollProperty] - (step * this.sliderItemOffset);
    
    this.slideScrollPosition = Math.max(0, Math.min(this.slideScrollPosition, this.slider[scrollOffset] - this.slider.clientHeight));
    
    this.slider.scrollTo({
      [this.isVertical ? 'top' : 'left']: this.slideScrollPosition
    });
  }
}
customElements.define('wave-slider-component', WaveSliderComponent);

class WaveThumbnailSlider extends WaveSliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();

    const variantItems = this.querySelectorAll('.thumbnail-list_item--variant:not(.is-active)');
    this.slider.dataset.mediaCount = this.sliderItems.length - variantItems.length;

    if(this.querySelector('[data-gang-option]')) {
      const inactiveGangItems = this.querySelectorAll('[data-gang-option]:not(.gang__active)');
      this.slider.dataset.mediaCount = this.sliderItems.length - inactiveGangItems.length;
    }

    if (this.slider.dataset.mediaCount <= 5) {
      this.classList.add('hide-arrow-buttons');
    }

    if (this.slider.dataset.mediaCount < 2) this.classList.add('hidden');
  }
}
customElements.define('wave-thumbnail-slider', WaveThumbnailSlider);

class WaveSiblingsComponent extends HTMLElement {
  constructor() {
    super();
    this.data = {};
    this.handle = this.dataset.handle;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 50
    });
  }

  async init() {
    await this.fetchData();
    this.injectElements();
  }

  async fetchData() {
    const res = await fetch(`${location.host}/products/${this.handle}/?sections=product-meta`);
    const resp = await res.json();
    const ele = document.createElement('div');
    ele.innerHTML = resp['product-meta'];
    // console.log(ele.querySelector('script').textContent)
    const data = JSON.parse(ele.querySelector('script').textContent);
    this.data = data;
  }

  createElement(wrap, sibling, id) {
    const link = document.createElement('a');
    link.href = `/products/${sibling.handle}`;

    const colorSwatch = document.createElement('boosted-color-swatch');
    colorSwatch.dataset.variantId = id;
    colorSwatch.dataset.title = sibling.title;
    colorSwatch.dataset.handle = sibling.handle;
    colorSwatch.dataset.collectionHandle = 'vauva-fw24';
    colorSwatch.dataset.featuredMedia = sibling.featured_media;
    colorSwatch.dataset.secondMedia = sibling.second_media;
    colorSwatch.dataset.price = sibling.price;
    colorSwatch.classList.add('color-swatch');
    colorSwatch.style.setProperty('--swatch-background-image', `url(${sibling.featured_media})`);
    colorSwatch.appendChild(link);

    wrap.appendChild(colorSwatch);
  }

  injectElements() {
    const wrap = document.createElement('div');
    wrap.classList.add('card__colors');
    wrap.classList.add('card__colors--large');

    const siblignEntries = Object.entries(this.data.siblings)

    let i = 0;
    const maxItem = 3;

    if (siblignEntries.length > 1) {
      this.createElement(wrap, this.data.siblings[this.id], this.id);

      for (const [id, sibling] of siblignEntries) {
        if (id != this.id) {
          if (i < maxItem - 1) {
            this.createElement(wrap, sibling, id)
          }

          if (i === maxItem -1) {
            const link = document.createElement('a');
            link.href = this.handle;
            link.classList.add('link', 'link--text', 'js-product-url');
            const s = document.createElement('small');
            s.textContent = `+${siblignEntries.length - maxItem}`;
            link.appendChild(s);
            wrap.appendChild(link);
          }

          i++;
        }
      }
    }

    this.appendChild(wrap);
  }
}

customElements.define('wave-siblings-component', WaveSiblingsComponent);

class BoostedColorSwatch extends ColorSwatch {
  constructor() {
    super();
  }

  colorSwatchFetch() {
    this.renderProductInfo();
  }

  updateButtons() {
    const container = this.closest('.boost-sd__product-item');

    const quickViewBtn = container.querySelector('quick-view-button');
    quickViewBtn.dataset.productUrl = `/products/${this.dataset.handle}`;
    const quickView = container.querySelector('quick-view');
    quickView.dataset.productUrl = `/products/${this.dataset.handle}`;
  }

  updateTitle() {
    const container = this.closest('.boost-sd__product-item');
    const title = container.querySelector('.boost-sd__product-title');
    title.textContent = this.dataset.title;
  }

  updatePrice() {
    const container = this.closest('.boost-sd__product-item');
    const price = container.querySelector('.boost-sd__format-currency');
    price.innerHTML = this.dataset.price;
  }

  colorSwatchChange() {
    const container = this.closest('.boost-sd__product-item');

    const productData = JSON.parse(container.dataset.product);
    productData.handle = this.dataset.handle;
    // check price?
    container.dataset.product = JSON.stringify(productData);

    const links = container.querySelectorAll('.boost-sd__product-link');
    links.forEach(link => {
      link.href = `/products/${this.dataset.handle}`
    })

    const mainImg = container.querySelector('.boost-sd__product-image-img--main');
    const secondImg = container.querySelector('.boost-sd__product-image-img--second');

    if (mainImg) {
      mainImg.src = this.dataset.featuredMedia;
      mainImg.srcset = ''
    }

    if (secondImg) {
      secondImg.src = this.dataset.secondMedia;
      secondImg.srcset = ''
    }

    const activeSwatch = container.querySelector('boosted-color-swatch.is-active');
    activeSwatch?.classList.remove('is-active');
    this.classList.add('is-active');

    this.updateButtons();
  }
}

customElements.define('boosted-color-swatch', BoostedColorSwatch);

